import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TokenService } from '../../core/services/auth/token.service';
import { NGXLogger } from 'ngx-logger';
import { RoutingUtils } from '../../routing/routing.utils';
import { ErrorService } from '../../core/services/error/error.service';
import { AuthStatus, DEFAULT_USER_AMILEARN_STATUS } from '../../core/services/user/user.type';
import { openProtectedRoutes } from '../../../environments/environment';
import { AppErrorCode } from '../../core/services/error/error.constants';
import { AppRoute } from '../../routing/routing.config';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';

@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss']
})
export class LoginDialogComponent implements OnInit, OnDestroy {
  AuthStatus = AuthStatus;
  authStatus$: BehaviorSubject<AuthStatus> = new BehaviorSubject<AuthStatus>(AuthStatus.IDLE);
  isProgressState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  queryParamsSub: Subscription;
  routerNativeSub: Subscription;
  authStatusSub: Subscription;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private tokenService: TokenService,
    private router: Router,
    private logger: NGXLogger,
    private routingUtils: RoutingUtils,
    private errorService: ErrorService,
    private analyticsService: AnalyticsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.logger.info('[AppComponent] ngOnInit() - start...');

    this.authStatusSub = this.authStatus$.subscribe((authStatus: AuthStatus) => {
      this.isProgressState$.next([AuthStatus.IDLE, AuthStatus.IN_PROGRESS].includes(authStatus));
    });

    this.logger.info('[AppComponent] Router event NavigationEnd sub.');

    // this.analyticsService.addToDom();

    const queryParamMap = this.route.snapshot.queryParamMap;

    // LOCAL development mode
    if (openProtectedRoutes(queryParamMap)) {
      this.logger.warn(
        'Open protected routes on local env. use ?prod-like=true param for test production flow on dev env.'
      );
      this.authStatus$.next(AuthStatus.SUCCESS);
      return this.cleanUp();
    }

    const codeParam = queryParamMap.get('code');
    if (codeParam) {
      // Set In_Progress for first run only
      if (this.authStatus$.value === AuthStatus.IDLE) {
        this.authStatus$.next(AuthStatus.IN_PROGRESS);
      }

      this.authStatus$.next(await this.authenticate(codeParam));

      if (this.authStatus$.value === AuthStatus.ERROR) {
        this.handleError(AppErrorCode.AUTH_ERROR);
        return this.cleanUp();
      }
    } else if (queryParamMap.get('error') || queryParamMap.get('error_description')) {
      this.authStatus$.next(AuthStatus.ERROR);
      this.handleError(AppErrorCode.AUTH_COGNITO_ERROR);
      return this.cleanUp();
    }

    const user = this.tokenService.storedAuthenticatedUser();
    if (!user) {
      this.authStatus$.next(AuthStatus.ERROR);
      this.router.navigate([AppRoute.WELCOME]);

      return this.cleanUp();
    }

    const isAuthenticated = await this.authService.isAuthenticated();
    this.logger.debug(
      '[AppComponent] ngOnInit() - isAuthenticated: ',
      isAuthenticated
    );

    this.authStatus$.next(isAuthenticated ? AuthStatus.SUCCESS : AuthStatus.ERROR);

    if (AuthStatus.ERROR === this.authStatus$.value) {
      this.handleError(AppErrorCode.AUTH_ERROR);
    } else {
      this.routeAuthenticatedUser();
    }
  }

  ngOnDestroy(): void {
    this.authStatusSub.unsubscribe();
  }

  private routeAuthenticatedUser(): void {
    // OPEN ROUTES
    // /privacy-policy & /terms-and-conditions shall be allowed to access
    // without further checks
    const appRoute = this.routingUtils.appRouteFromPath(location.pathname);
    if (appRoute && this.routingUtils.isOpenRoute(appRoute)) {
      this.router.navigate([appRoute]);
      return;
    }

    // PROTECTED ROUTES
    const payload = this.tokenService.idTokenPayload();
    if (!payload || !payload.amilearnStatus) {
      // Routing user to AppRoute.NEW_USER as we don't know what to do
      this.logger.warn(
        '[AppComponent] routeAuthenticatedUser() - ERROR - payload or payload.amilearnStatus is undefined\n Routing user to ',
        this.routingUtils.routeForUserStatus(DEFAULT_USER_AMILEARN_STATUS)
      );
      this.router.navigate([this.routingUtils.routeForUserStatus(DEFAULT_USER_AMILEARN_STATUS)]);
      return;
    }

    const route = this.routingUtils.routeForUserStatus(payload.amilearnStatus);

    this.logger.debug(
      '[AppComponent] routeAuthenticatedUser() - amilearnStatus: ',
      payload.amilearnStatus
    );
    this.logger.debug(
      '[AppComponent] routeAuthenticatedUser() - routing to: ',
      route
    );

    this.router.navigate([route]);
  }

  private handleError(errorCode = AppErrorCode.AUTH_ERROR): void {
    this.logger.debug('[AppComponent] handleError() - redirecting to ERROR_PAGE, errorCode: ', errorCode);
    // this.shouldNavigate = false;
    this.errorService.routeToAuthError(errorCode);
  }

  private async authenticate(code: string): Promise<AuthStatus> {
    this.logger.info('[AppComponent] authenticate() - starting Authentication flow');

    const authStatus = await this.authService.authenticate(code);
    this.logger.info(
      `[AppComponent] authenticate() - Authentication result - ${authStatus}`
    );

    return authStatus;
  }
  private cleanUp(): void {
    this.logger.info('[AppComponent] Router event watcher - cleanup.');
    this.queryParamsSub?.unsubscribe();
  }
}
